import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Typography } from '@mui/material';
import { getDataPurgeJobs, processPurgeJob } from '../api/dataLifecycleApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';

const { semantic } = colorTokens;

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const rowActionSx = {
  minHeight: 32,
  px: 1,
};

function jobStatusTone(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'completed' || normalized === 'purged' || normalized === 'success') return 'Success';
  if (normalized === 'pending' || normalized === 'approved' || normalized === 'scheduled') return 'Warning';
  if (normalized === 'failed' || normalized === 'error' || normalized === 'cancelled' || normalized === 'canceled') {
    return 'Danger';
  }
  if (normalized === 'processing' || normalized === 'running') return 'Info';
  return 'Neutral';
}

function formatDate(value, emptyValue) {
  if (!value) return emptyValue;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

export function DataPurgeJobsPage() {
  const { t } = useTranslation(['dataLifecycle', 'common']);
  const { session } = useAuth();
  const canPurge = hasPermission(session, 'system.data_lifecycle.purge');

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const physicalPurgeEnabled = jobs[0]?.physical_purge_enabled === true;

  const emptyValue = t('common:empty_value');
  const recordCount = jobs.length;

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getDataPurgeJobs({ per_page: 100 });
      setJobs(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const handleProcess = useCallback(async (job) => {
    setMessage('');

    try {
      const result = await processPurgeJob(job.id);
      setMessage(result.message || t('dataLifecycle:purge_jobs.job_processed', { status: result.status }));
      loadJobs();
    } catch (err) {
      setMessage(err?.response?.data?.message || err.message || t('dataLifecycle:purge_jobs.process_failed'));
    }
  }, [loadJobs, t]);

  const columns = useMemo(() => [
    {
      id: 'id',
      label: t('dataLifecycle:purge_jobs.columns.job'),
      render: (row) => (
        row.id != null
          ? <Typography sx={monoSx}>{row.id}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'entity_type',
      label: t('dataLifecycle:recycle_bin.columns.entity'),
      render: (row) => (
        row.entity_type
          ? t(`dataLifecycle:entity_types.${row.entity_type}`, { defaultValue: row.entity_type })
          : emptyValue
      ),
    },
    {
      id: 'entity_id',
      label: t('common:id'),
      render: (row) => (
        row.entity_id != null && row.entity_id !== ''
          ? <Typography sx={monoSx}>{row.entity_id}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (row) => (
        row.status
          ? (
            <StatusBadge
              status={row.status}
              category="generic"
              apiLabel={row.status}
              size="Sm"
              tone={jobStatusTone(row.status)}
            />
          )
          : emptyValue
      ),
    },
    {
      id: 'purge_after_at',
      label: t('dataLifecycle:purge_jobs.columns.purge_after'),
      render: (row) => formatDate(row.purge_after_at, emptyValue),
    },
    {
      id: 'failure_reason',
      label: t('dataLifecycle:purge_jobs.columns.failure'),
      render: (row) => (
        <Typography
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontSize: 13,
            lineHeight: '20px',
            color: semantic.text.secondary,
            maxWidth: 260,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          {row.failure_reason || emptyValue}
        </Typography>
      ),
    },
    {
      id: 'created_at',
      label: t('common:created'),
      render: (row) => formatDate(row.created_at, emptyValue),
    },
    {
      id: 'actions',
      label: t('common:actions'),
      align: 'right',
      render: (row) => {
        if (!(canPurge && ['pending', 'approved'].includes(row.status))) {
          return emptyValue;
        }

        return (
          <Button
            variant="Secondary"
            onClick={() => handleProcess(row)}
            sx={rowActionSx}
          >
            {t('dataLifecycle:purge_jobs.process')}
          </Button>
        );
      },
    },
  ], [canPurge, emptyValue, handleProcess, t]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('dataLifecycle:purge_jobs.title')}
        subtitle={t('dataLifecycle:purge_jobs.subtitle')}
      />

      {!physicalPurgeEnabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('dataLifecycle:purge_jobs.physical_purge_disabled')}
        </Alert>
      )}

      {message && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadJobs} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('dataLifecycle:purge_jobs.load_failed')}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('dataLifecycle:purge_jobs.jobs')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('dataLifecycle:purge_jobs.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : !error ? (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={jobs}
            getRowKey={(row) => row.id}
            emptyMessage={t('common:no_data_found')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        ) : null}
      </SectionCard>
    </Box>
  );
}
